import { useState, useEffect } from 'react';
import { useOutletContext, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Megaphone, Facebook, Instagram, Linkedin, ArrowLeft,
  Plus, Check, X, ExternalLink, AlertTriangle, Loader2,
  CheckCircle, XCircle, Settings, RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

// Google icon component
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const platforms = [
  {
    id: 'facebook',
    name: 'Facebook',
    icon: Facebook,
    color: 'bg-blue-600',
    hoverColor: 'hover:bg-blue-700',
    description: 'Publiez du contenu, repondez aux commentaires et messages',
    features: ['Publications', 'Commentaires', 'Messages prives', 'Statistiques'],
    oauthEndpoint: '/api/social/facebook/auth',
    setupUrl: 'https://developers.facebook.com/',
    setupSteps: [
      'Allez sur developers.facebook.com et connectez-vous',
      'Cliquez sur "Creer une app" > Type "Business"',
      'Note ton App ID et App Secret',
      'Dans Parametres > OAuth, ajoutez l\'URL de callback',
      'Ajoutez les produits "Facebook Login" et "Instagram Graph API"'
    ]
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: Instagram,
    color: 'bg-gradient-to-tr from-purple-600 via-pink-600 to-orange-500',
    hoverColor: 'hover:opacity-90',
    description: 'Gère ton compte Instagram Business',
    features: ['Publications', 'Commentaires', 'Messages (via FB)', 'Statistiques'],
    oauthEndpoint: null, // Instagram uses Facebook OAuth
    linkedTo: 'facebook',
    setupUrl: 'https://developers.facebook.com/',
    setupSteps: [
      'Connectez d\'abord Facebook (meme credentials)',
      'Lie ton compte Instagram Business à ta Page Facebook',
      'Dans Instagram > Parametres > Compte > Comptes lies',
      'Sélectionne ta Page Facebook',
      'Les comptes Instagram apparaitront automatiquement'
    ]
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: Linkedin,
    color: 'bg-blue-700',
    hoverColor: 'hover:bg-blue-800',
    description: 'Publie sur ton profil ou page entreprise',
    features: ['Publications', 'Commentaires', 'Statistiques'],
    oauthEndpoint: '/api/social/linkedin/auth',
    setupUrl: 'https://www.linkedin.com/developers/',
    setupSteps: [
      'Allez sur linkedin.com/developers et connectez-vous',
      'Cliquez sur "Creer une app"',
      'Note ton Client ID et Client Secret',
      'Dans "Auth", ajoutez l\'URL de callback OAuth 2.0',
      'Ajoutez les produits "Sign In with LinkedIn" et "Share on LinkedIn"'
    ]
  },
  {
    id: 'google_business',
    name: 'Google Business',
    icon: () => <GoogleIcon className="w-6 h-6" />,
    color: 'bg-white border-2 border-slate-200',
    hoverColor: 'hover:border-slate-300 hover:bg-slate-50',
    textColor: 'text-slate-700',
    description: 'Repondez aux avis et publiez des actualites',
    features: ['Avis clients', 'Posts locaux', 'Informations'],
    oauthEndpoint: '/api/social/google-business/auth',
    setupUrl: 'https://console.cloud.google.com/',
    setupSteps: [
      'Allez sur console.cloud.google.com',
      'Activez les APIs Google Business Profile',
      'Creez des identifiants OAuth 2.0',
      'Ajoutez le scope business.manage',
      'Vérifie que ta fiche Google Business est revendiquée'
    ]
  }
];

export default function SocialAccountsPage() {
  const { user } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [configStatus, setConfigStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [showSetupModal, setShowSetupModal] = useState(null);

  useEffect(() => {
    fetchAccounts();
    fetchConfigStatus();
    
    // Handle OAuth callback results
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const accountsCount = searchParams.get('accounts');
    
    if (success) {
      const platformName = platforms.find(p => p.id === success)?.name || success;
      toast.success(`${platformName} connecte avec succes !${accountsCount ? ` (${accountsCount} compte(s))` : ''}`);
      searchParams.delete('success');
      searchParams.delete('accounts');
      setSearchParams(searchParams);
      fetchAccounts();
    }
    
    if (error) {
      toast.error(`Erreur de connexion: ${error}`);
      searchParams.delete('error');
      setSearchParams(searchParams);
    }
  }, []);

  const fetchConfigStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/social/config-status`);
      if (response.ok) {
        setConfigStatus(await response.json());
      }
    } catch (error) {
      console.error('Error fetching config status:', error);
    }
  };

  const fetchAccounts = async () => {
    const token = localStorage.getItem('client_token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/marketing/accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setConnectedAccounts(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const startOAuthFlow = async (platform) => {
    if (!platform.oauthEndpoint) {
      // Instagram uses Facebook OAuth
      if (platform.linkedTo === 'facebook') {
        const fbPlatform = platforms.find(p => p.id === 'facebook');
        startOAuthFlow(fbPlatform);
      }
      return;
    }

    setConnecting(platform.id);

    try {
      const response = await fetch(`${API_URL}${platform.oauthEndpoint}?user_id=${user.id}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.auth_url) {
          // Redirect to OAuth provider
          window.location.href = data.auth_url;
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'initialisation OAuth');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setConnecting(null);
    }
  };

  const connectAccount = async (platform) => {
    const isConfigured = configStatus[platform.id];
    
    if (!isConfigured) {
      // Show setup instructions if not configured
      setShowSetupModal(platform.id);
      return;
    }
    
    // Start OAuth flow
    startOAuthFlow(platform);
  };

  const disconnectAccount = async (accountId, platformName) => {
    if (!confirm(`Deconnecter ${platformName} ?`)) return;

    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/marketing/accounts/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Compte deconnecte');
        fetchAccounts();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const isConnected = (platformId) => {
    return connectedAccounts.some(a => a.platform === platformId);
  };

  const getConnectedAccount = (platformId) => {
    return connectedAccounts.find(a => a.platform === platformId);
  };

  const getAccountsForPlatform = (platformId) => {
    return connectedAccounts.filter(a => a.platform === platformId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-accounts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/espace-client/marketing">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-rm-text">Comptes reseaux sociaux</h1>
            <p className="text-sm text-slate-500">Connecte tes comptes pour gérer ta présence en ligne</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchAccounts(); fetchConfigStatus(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Configuration Status Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-blue-800">Statut de configuration</p>
            <div className="flex flex-wrap gap-3 mt-2">
              {platforms.filter(p => p.oauthEndpoint).map(platform => (
                <div key={platform.id} className="flex items-center gap-1.5 text-sm">
                  {configStatus[platform.id] ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className={configStatus[platform.id] ? 'text-green-700' : 'text-red-600'}>
                    {platform.name}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-blue-600 mt-2">
              Les plateformes non configurees necessitent des identifiants API. Cliquez sur "Connecter" pour voir les instructions.
            </p>
          </div>
        </div>
      </div>

      {/* Connected Accounts Summary */}
      {connectedAccounts.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-800">{connectedAccounts.length} compte(s) connecte(s)</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {connectedAccounts.map(account => (
              <span key={account.id} className="bg-white px-3 py-1 rounded-full text-sm border border-green-200 flex items-center gap-2">
                {account.platform_picture_url && (
                  <img src={account.platform_picture_url} alt="" className="w-5 h-5 rounded-full" />
                )}
                {account.platform_name || account.platform}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Platforms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {platforms.map((platform) => {
          const connected = isConnected(platform.id);
          const accounts = getAccountsForPlatform(platform.id);
          const Icon = platform.icon;
          const isConfigured = configStatus[platform.id] || platform.linkedTo;

          return (
            <motion.div
              key={platform.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${
                connected ? 'border-green-300' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${platform.color}`}>
                      {typeof Icon === 'function' ? <Icon /> : <Icon className="w-6 h-6" />}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-rm-text">{platform.name}</h3>
                      {connected && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          {accounts.length} compte(s) connecte(s)
                        </span>
                      )}
                      {!connected && platform.linkedTo && (
                        <span className="text-xs text-slate-500">
                          Via {platforms.find(p => p.id === platform.linkedTo)?.name}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {!connected ? (
                    <Button
                      size="sm"
                      className={`${platform.color} ${platform.hoverColor} ${platform.textColor || 'text-white'}`}
                      onClick={() => connectAccount(platform)}
                      disabled={connecting === platform.id}
                    >
                      {connecting === platform.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-1" />
                          Connecter
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => connectAccount(platform)}
                        disabled={connecting === platform.id}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Ajouter
                      </Button>
                    </div>
                  )}
                </div>

                <p className="text-sm text-slate-600 mb-4">{platform.description}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {platform.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Connected accounts list */}
                {accounts.length > 0 && (
                  <div className="border-t border-slate-100 pt-4 space-y-2">
                    {accounts.map(account => (
                      <div key={account.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-2">
                        <div className="flex items-center gap-2">
                          {account.platform_picture_url && (
                            <img src={account.platform_picture_url} alt="" className="w-8 h-8 rounded-full" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-slate-700">{account.platform_name}</p>
                            {account.platform_username && (
                              <p className="text-xs text-slate-500">@{account.platform_username}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => disconnectAccount(account.id, account.platform_name)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Configuration hint */}
                {!isConfigured && !platform.linkedTo && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-700">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      Configuration requise. Cliquez sur "Connecter" pour les instructions.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Setup Modal */}
      {showSetupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
          >
            {(() => {
              const platform = platforms.find(p => p.id === showSetupModal);
              const Icon = platform?.icon;
              const isConfigured = configStatus[platform?.id];
              
              return (
                <>
                  <div className={`p-6 ${platform?.textColor ? 'text-slate-800' : 'text-white'} ${platform?.color}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 ${platform?.textColor ? 'bg-slate-100' : 'bg-white/20'} rounded-xl flex items-center justify-center`}>
                        {typeof Icon === 'function' ? <Icon /> : <Icon className={`w-6 h-6 ${platform?.textColor || ''}`} />}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">Connecter {platform?.name}</h3>
                        <p className={`text-sm ${platform?.textColor ? 'text-slate-600' : 'text-white/80'}`}>
                          {isConfigured ? 'Pret a connecter' : 'Configuration requise'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    {isConfigured ? (
                      <div className="text-center py-4">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                        <p className="text-slate-600 mb-4">
                          La plateforme est configuree. Cliquez sur "Se connecter" pour autoriser l'acces.
                        </p>
                        <Button 
                          className="w-full"
                          onClick={() => {
                            setShowSetupModal(null);
                            startOAuthFlow(platform);
                          }}
                        >
                          Se connecter avec {platform?.name}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-sm text-amber-800">
                            <AlertTriangle className="w-4 h-4 inline mr-1" />
                            Les identifiants API ne sont pas configures pour cette plateforme.
                          </p>
                        </div>

                        <h4 className="font-semibold text-rm-text mb-3">Etapes de configuration :</h4>
                        <ol className="space-y-3">
                          {platform?.setupSteps.map((step, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <span className="w-6 h-6 bg-rm-primary text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                                {idx + 1}
                              </span>
                              <span className="text-sm text-slate-600">{step}</span>
                            </li>
                          ))}
                        </ol>

                        <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-600 mb-2">
                            <strong>URL de callback a configurer :</strong>
                          </p>
                          <code className="text-xs bg-slate-200 px-2 py-1 rounded block break-all">
                            {API_URL}/api/social/{platform?.id.replace('_', '-')}/callback
                          </code>
                        </div>

                        <a
                          href={platform?.setupUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center gap-2 text-sm text-rm-primary hover:underline"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Ouvrir la console developpeur {platform?.name}
                        </a>

                        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>Besoin d'aide ?</strong> Une fois les identifiants créés, tu devras les ajouter aux variables d'environnement du serveur :
                          </p>
                          <ul className="text-xs text-blue-700 mt-2 space-y-1">
                            {platform?.id === 'facebook' && (
                              <>
                                <li>FACEBOOK_APP_ID=votre_app_id</li>
                                <li>FACEBOOK_APP_SECRET=votre_app_secret</li>
                              </>
                            )}
                            {platform?.id === 'linkedin' && (
                              <>
                                <li>LINKEDIN_CLIENT_ID=votre_client_id</li>
                                <li>LINKEDIN_CLIENT_SECRET=votre_client_secret</li>
                              </>
                            )}
                            {platform?.id === 'google_business' && (
                              <>
                                <li>GOOGLE_CLIENT_ID=votre_client_id</li>
                                <li>GOOGLE_CLIENT_SECRET=votre_client_secret</li>
                              </>
                            )}
                          </ul>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowSetupModal(null)}>
                      Fermer
                    </Button>
                  </div>
                </>
              );
            })()}
          </motion.div>
        </div>
      )}
    </div>
  );
}
